
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCheck, Lock, ArrowRight } from 'lucide-react';
import FadeIn from '../../../components/FadeIn';
import SEO from '../../../components/SEO';

const ClosingDeals = () => {
    return (
        <div className="bg-white">
            <SEO
                title="Closing Six-Figure Deals Training | BPO Consultants"
                description="Learn the mechanics of closing large enterprise contracts. Procurement navigation, legal reviews, and final sign-off."
                keywords="Closing Techniques, Enterprise Sales Closing, High Ticket Sales, Sales Psychology, Deal Closing Strategies"
            />

            {/* Hero */}
            <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#0B0D15] overflow-hidden">
                <div className="absolute inset-0 opacity-20">
                    <img src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=1200" alt="Closing Handshake" className="w-full h-full object-cover" />
                </div>
                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <FadeIn>
                        <span className="inline-block bg-[#A2CB52]/20 text-[#A2CB52] px-4 py-2 rounded-full text-sm font-bold border border-[#A2CB52]/40 mb-6 backdrop-blur-sm">
                            <Lock size={14} className="inline mr-1" /> Module 4: The Close
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Closing <br /> Six-Figure Deals
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                            The "close" isn't an event, it's a process. Learn to drive it across the finish line.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <FadeIn className="mb-16">
                        <h2 className="text-3xl font-bold text-[#20222D] mb-6">Driving the Commitment</h2>
                        <p className="text-lg text-gray-600 leading-relaxed mb-8">
                            Closing large ACV deals requires more than just asking "credit or debit?". It requires navigating complex legal reviews, security audits, and boardroom sign-offs. We teach you how to manage the "last mile" of the marathon.
                        </p>

                        <div className="grid md:grid-cols-2 gap-8">
                            {[
                                {
                                    title: "The Mutual Action Plan (MAP)",
                                    desc: "Co-creating a timeline with your prospect to work backwards from their go-live date to signature."
                                },
                                {
                                    title: "Risk Reversal",
                                    desc: "Strategies like pilot clauses and opt-outs that make it safe for the prospect to say 'Yes'."
                                },
                                {
                                    title: "Handling Last Minute Objections",
                                    desc: "What to do when the CEO steps in at the 11th hour to kill the deal."
                                },
                                {
                                    title: "Urgency vs. Pressure",
                                    desc: "Creating genuine urgency through Compelling Events rather than artificial sales tactics."
                                }
                            ].map((item, i) => (
                                <div key={i} className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                                    <div className="text-[#A2CB52] mb-4">
                                        <CheckCheck size={28} />
                                    </div>
                                    <h3 className="text-xl font-bold text-[#20222D] mb-2">{item.title}</h3>
                                    <p className="text-gray-600 text-sm">{item.desc}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>

                    <FadeIn className="mb-16 bg-gradient-to-r from-gray-900 to-gray-800 p-8 rounded-2xl text-white">
                        <h3 className="text-2xl font-bold mb-4">Statistic</h3>
                        <p className="text-gray-300 italic mb-4">
                            "Top performing closers don't use 'closing techniques'. They use 'commitment advancing questions' throughout the entire sales cycle."
                        </p>
                    </FadeIn>

                    <FadeIn className="bg-[#20222D] rounded-3xl p-12 text-center relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-[#A2CB52] blur-[100px] opacity-20 pointer-events-none"></div>
                        <h2 className="text-3xl font-bold text-white mb-6">Become a Closer</h2>
                        <Link to="/contact" className="inline-flex items-center bg-[#A2CB52] text-[#20222D] px-8 py-4 rounded-xl font-bold text-lg hover:bg-[#B5D575] transition-all transform hover:scale-105">
                            Start Module <ArrowRight className="ml-2" size={20} />
                        </Link>
                    </FadeIn>
                </div>
            </section>
        </div>
    );
};

export default ClosingDeals;
